import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import PasswordInput from "@/components/base/PasswordInput";
import { supabase } from "@/lib/supabase";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let active = true;
    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!active) return;
        if (!data.session) {
          setInvalid(true);
        }
        setChecking(false);
      })
      .catch(() => {
        if (!active) return;
        setInvalid(true);
        setChecking(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const handleSubmit = async () => {
    setError("");
    if (password.length < 6) {
      setError("Mật khẩu phải có ít nhất 6 ký tự.");
      return;
    }
    if (password !== confirm) {
      setError("Mật khẩu nhập lại không khớp.");
      return;
    }
    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password,
      });
      if (updateError) {
        setError(updateError.message);
        return;
      }
      await supabase.auth.signOut();
      setDone(true);
    } catch {
      setError("Có lỗi xảy ra. Vui lòng thử lại.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="w-full min-h-screen bg-background-50 pt-28 md:pt-32 pb-20 px-4 md:px-8">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
              <i className="ri-key-2-line text-2xl" />
            </div>
            <h1 className="font-display text-3xl font-bold text-foreground-950 mb-2">
              Đặt lại mật khẩu
            </h1>
            <p className="text-sm text-foreground-600 font-body">
              Nhập mật khẩu mới cho tài khoản của bạn
            </p>
          </div>

          <div className="bg-white rounded-lg border border-background-200/70 p-5 md:p-6">
            {checking ? (
              <div className="flex items-center justify-center py-16">
                <i className="ri-loader-4-line animate-spin text-2xl text-primary-500" />
              </div>
            ) : done ? (
              <div className="text-center py-6">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                  <i className="ri-checkbox-circle-line text-2xl" />
                </div>
                <p className="text-sm text-foreground-700 font-body mb-6">
                  Mật khẩu đã được đặt lại thành công!
                </p>
                <Link
                  to="/login"
                  className="inline-block w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-label text-sm font-medium whitespace-nowrap rounded-md transition-colors cursor-pointer text-center"
                >
                  Đăng nhập ngay
                </Link>
              </div>
            ) : invalid ? (
              <div className="text-center py-6">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                  <i className="ri-error-warning-line text-2xl" />
                </div>
                <p className="text-sm text-foreground-700 font-body mb-6">
                  Link đặt lại mật khẩu không hợp lệ hoặc đã hết hạn. Vui lòng thực hiện lại bước quên mật khẩu.
                </p>
                <Link
                  to="/login"
                  className="inline-block w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-label text-sm font-medium whitespace-nowrap rounded-md transition-colors cursor-pointer text-center"
                >
                  Quay lại đăng nhập
                </Link>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit();
                }}
                className="space-y-4"
              >
                <div>
                  <label
                    htmlFor="new-password"
                    className="block text-sm font-label font-medium text-foreground-800 mb-1.5"
                  >
                    Mật khẩu mới
                  </label>
                  <PasswordInput
                    id="new-password"
                    name="password"
                    value={password}
                    onChange={setPassword}
                    placeholder="Ít nhất 6 ký tự"
                    autoComplete="new-password"
                  />
                </div>
                <div>
                  <label
                    htmlFor="confirm-password"
                    className="block text-sm font-label font-medium text-foreground-800 mb-1.5"
                  >
                    Nhập lại mật khẩu mới
                  </label>
                  <PasswordInput
                    id="confirm-password"
                    name="confirm"
                    value={confirm}
                    onChange={setConfirm}
                    placeholder="Nhập lại mật khẩu mới"
                    autoComplete="new-password"
                  />
                </div>

                {error && (
                  <div className="px-4 py-3 bg-accent-50 border border-accent-200 rounded-md text-sm text-accent-700 font-body flex items-start gap-2">
                    <i className="ri-error-warning-line mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 font-label text-sm font-medium whitespace-nowrap rounded-md transition-colors cursor-pointer"
                >
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2" />
                      Đang xử lý...
                    </>
                  ) : (
                    "Đặt lại mật khẩu"
                  )}
                </button>
              </form>
            )}
          </div>

          <p className="text-center text-sm text-foreground-500 font-body mt-6">
            <Link to="/login" className="text-primary-700 font-medium hover:underline">
              Quay lại đăng nhập
            </Link>
          </p>
        </div>
      </div>
    </MainLayout>
  );
}